import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';
import { User } from '../../core/models/user.model';
import { UserRoleEnum } from '../../core/models/user-role.enum';
import { OrganizationModuleEnum } from '../../core/models/organization-module.enum';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { NotificationDropdownComponent } from '../../notifications/components/notification-dropdown/notification-dropdown.component';
import { NotificationApiService } from '../../notifications/services/notification.api.service';
import { toSignal } from '@angular/core/rxjs-interop';

interface SidebarItem {
    id: string;
    labelKey: string;
    icon: string;
    route: string;
    queryParams?: Record<string, string | boolean | number>;
    exact?: boolean;
    roles?: UserRoleEnum[];
    subNested?: boolean;
    // For items that aren't gated by global role alone — e.g. TEAM_LEAD, which is team-scoped
    // and lives on User.teamMemberships rather than UserRoleEnum.
    showIf?: (user: User) => boolean;
}

interface SidebarSection {
    id: string;
    titleKey: string;
    pinned: boolean;
    roles?: UserRoleEnum[];
    modules?: OrganizationModuleEnum[];
    items: SidebarItem[];
}

@Component({
    selector: 'app-main-layout',
    standalone: true,
    imports: [CommonModule, RouterModule, TranslatePipe, NotificationDropdownComponent],
    templateUrl: './main-layout.component.html',
    styleUrl: './main-layout.component.scss'
})
export class MainLayoutComponent {
    private static readonly SECTION_STORAGE_KEY_PREFIX = 'sidebar-sections:';

    UserRoleEnum = UserRoleEnum;

    private readonly router = inject(Router);
    private readonly authService = inject(AuthService);
    private readonly notificationApiService = inject(NotificationApiService);

    public isSidebarOpen = signal(false);
    public isUserDropdownOpen = signal(false);
    public showAlphaBanner = signal(true);
    public expandedSections = signal<Record<string, boolean>>({});

    // Pinned notifications (e.g. usage-limit alerts) shown as a persistent top banner until they
    // expire server-side. Dismissal here only hides them for this browser session.
    private readonly dismissedPinnedIds = signal<Set<string>>(new Set());
    public pinnedNotifications = computed(() =>
        this.notificationApiService.pinnedNotifications().filter(n => !this.dismissedPinnedIds().has(n.id))
    );

    private readonly loadedSectionStateForUserId = signal<string | null>(null);
    private readonly currentUrl = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url)
        ),
        { initialValue: this.router.url }
    );

    public currentUser = toSignal(this.authService.currentUser$, { initialValue: null as User | null });
    public sections: SidebarSection[] = [
        {
            id: 'speech-analysis',
            titleKey: 'layout.menu.speechAnalysis',
            pinned: false,
            modules: [OrganizationModuleEnum.SPEECH_ANALYSIS],
            items: [
                {
                    id: 'speech-analysis',
                    labelKey: 'pages.speechAnalysis.title',
                    icon: 'ri-mic-line',
                    route: '/speech-analysis'
                },
                {
                    id: 'new-speech-analysis-sub',
                    labelKey: 'pages.newSpeechAnalysis.title',
                    icon: 'ri-add-line',
                    route: '/speech-analysis/new',
                    exact: true,
                    subNested: true
                },
                {
                    id: 'content-documents',
                    labelKey: 'pages.contentDocuments.title',
                    icon: 'ri-file-list-3-line',
                    route: '/content-documents'
                },
                {
                    id: 'speech-scripts',
                    labelKey: 'pages.speechScripts.title',
                    icon: 'ri-megaphone-line',
                    route: '/speech-scripts'
                },
                {
                    id: 'speech-analysis-categories',
                    labelKey: 'pages.speechAnalysisCategories.menuItemTitle',
                    icon: 'ri-price-tag-3-line',
                    route: '/speech-analysis-categories',
                    exact: true,
                    roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPER_ADMIN],
                },
                {
                    id: 'speech-analysis-templates',
                    labelKey: 'pages.speechAnalysisTemplates.menuItemTitle',
                    icon: 'ri-file-list-3-line',
                    route: '/speech-analysis-templates',
                    exact: true,
                    roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPER_ADMIN],
                }
            ]
        },
        {
            id: 'conversation-analysis',
            titleKey: 'layout.menu.conversationAnalysis',
            pinned: false,
            modules: [OrganizationModuleEnum.CONVERSATION_ANALYSIS],
            items: [
                {
                    id: 'conversation-analysis',
                    labelKey: 'pages.conversationAnalysis.title',
                    icon: 'ri-customer-service-2-line',
                    route: '/conversation-analysis'
                },
                {
                    id: 'new-conversation-analysis-sub',
                    labelKey: 'pages.newConversationAnalysis.title',
                    icon: 'ri-add-line',
                    route: '/conversation-analysis/new',
                    exact: true,
                    subNested: true
                },
                {
                    id: 'conversation-templates',
                    labelKey: 'pages.conversationTemplates.menuItemTitle',
                    icon: 'ri-text-block',
                    route: '/conversation-templates',
                    exact: false,
                    roles: [UserRoleEnum.SUPER_ADMIN]
                }
            ]
        },
        {
            id: 'administration',
            titleKey: 'layout.menu.administration',
            pinned: false,
            roles: [UserRoleEnum.ADMIN, UserRoleEnum.SUPER_ADMIN],
            items: [
                {
                    id: 'organization',
                    labelKey: 'pages.organization.title',
                    icon: 'ri-building-4-line',
                    route: '/organization',
                    exact: true
                },
                {
                    id: 'teams',
                    labelKey: 'pages.teams.title',
                    icon: 'ri-team-line',
                    route: '/teams',
                    exact: true
                },
                {
                    id: 'users',
                    labelKey: 'pages.users.title',
                    icon: 'ri-group-line',
                    route: '/users',
                    exact: true
                },
                {
                    id: 'registration-links',
                    labelKey: 'pages.registrationLinks.title',
                    icon: 'ri-link',
                    route: '/registration-links',
                    exact: true
                },
                {
                    id: 'administration-documents',
                    labelKey: 'pages.organizationDocuments.title',
                    icon: 'ri-file-list-3-line',
                    route: '/administration/documents',
                    exact: true
                }
            ]
        },
        {
            id: 'admin',
            titleKey: 'layout.menu.admin',
            pinned: false,
            roles: [UserRoleEnum.SUPER_ADMIN],
            items: [
                {
                    id: 'organizations',
                    labelKey: 'pages.organizations.title',
                    icon: 'ri-building-2-line',
                    route: '/organizations',
                    exact: true
                },
                {
                    id: 'ai-models',
                    labelKey: 'pages.aiModels.title',
                    icon: 'ri-robot-2-line',
                    route: '/ai-models',
                    exact: true
                },
                {
                    id: 'ai-usage',
                    labelKey: 'pages.aiUsage.title',
                    icon: 'ri-coin-line',
                    route: '/ai-usage',
                    exact: true
                },
                {
                    id: 'conversation-exports',
                    labelKey: 'pages.conversationExports.title',
                    icon: 'ri-file-pdf-2-line',
                    route: '/conversation-exports',
                    exact: true
                },
                {
                    id: 'ai-settings',
                    labelKey: 'pages.aiSettings.title',
                    icon: 'ri-settings-3-line',
                    route: '/ai-settings',
                    exact: true
                }
            ]
        },
        {
            id: 'feedback',
            titleKey: 'layout.menu.feedback',
            pinned: false,
            roles: [UserRoleEnum.SUPER_ADMIN],
            items: [
                {
                    id: 'all-feedbacks',
                    labelKey: 'pages.feedbackGeneral.title',
                    icon: 'ri-feedback-line',
                    route: '/feedbacks',
                    queryParams: { showAll: true },
                    roles: [UserRoleEnum.SUPER_ADMIN]
                },
                {
                    id: 'speech-analysis-feedback',
                    labelKey: 'pages.speechAnalysisFeedback.title',
                    icon: 'ri-feedback-line',
                    route: '/admin/speech-analysis-feedback',
                    exact: true
                },
            ]
        },
        {
            id: 'user',
            titleKey: 'layout.menu.user',
            pinned: true,
            items: [
                {
                    id: 'profile',
                    labelKey: 'pages.userProfile.title',
                    icon: 'ri-user-3-line',
                    route: '/profile',
                    exact: true
                },
                {
                    id: 'feedbacks',
                    labelKey: 'pages.feedbackGeneral.title',
                    icon: 'ri-feedback-line',
                    route: '/feedbacks',
                    roles: [UserRoleEnum.USER, UserRoleEnum.ADMIN]
                },
                {
                    id: 'my-teams',
                    labelKey: 'pages.teams.myTeams',
                    icon: 'ri-team-line',
                    route: '/teams',
                    exact: true,
                    // TEAM_LEAD is team-scoped, not a UserRoleEnum value — gate on teamMemberships
                    // instead of `roles`. ADMIN/SUPER_ADMIN already have Teams via the
                    // administration section above, so this only needs to surface it for leads.
                    showIf: user => !!user.teamMemberships?.some(m => m.role === 'LEAD')
                }
            ]
        }
    ];
    public visibleSections = computed(() => {
        const user = this.currentUser();

        if (!user) {
            return [] as SidebarSection[];
        }

        const filtered = this.sections
            .filter(section => !section.roles || section.roles.includes(user.role))
            .filter(section => !section.modules || section.modules.some(m => user.modules?.includes(m)))
            .map(section => ({
                ...section,
                items: section.items
                    .filter(item => !item.roles || item.roles.includes(user.role))
                    .filter(item => !item.showIf || item.showIf(user))
            }))
            .filter(section => section.items.length > 0);

        // When only one module section is visible, there's nothing to collapse it in favor
        // of - pin it open like the always-visible sections, and don't let it be toggled.
        const visibleModuleSectionCount = filtered.filter(section => section.modules).length;
        if (visibleModuleSectionCount === 1) {
            return filtered.map(section =>
                section.modules ? { ...section, pinned: true } : section
            );
        }

        return filtered;
    });

    protected isMockLogin$: Observable<boolean> = this.authService.isMockLogin$;

    constructor() {
        effect(
            () => {
                const user = this.currentUser();

                if (!user) {
                    this.expandedSections.set({});
                    this.loadedSectionStateForUserId.set(null);
                    return;
                }

                if (this.loadedSectionStateForUserId() === user.id) {
                    return;
                }

                const persistedState = this.readStoredSectionState(user.id);
                const nextState = this.visibleSections().reduce<Record<string, boolean>>((state, section) => {
                    state[section.id] = section.pinned ? true : persistedState[section.id] ?? false;
                    return state;
                }, {});

                this.expandedSections.set(nextState);
                this.loadedSectionStateForUserId.set(user.id);
            },
            { allowSignalWrites: true }
        );

        effect(
            () => {
                const activeSectionId = this.findActiveSectionId(this.visibleSections(), this.currentUrl());

                if (!activeSectionId || this.expandedSections()[activeSectionId]) {
                    return;
                }

                const nextState = {
                    ...this.expandedSections(),
                    [activeSectionId]: true
                };

                this.expandedSections.set(nextState);

                const user = this.currentUser();
                if (user) {
                    this.persistSectionState(user.id, nextState);
                }
            },
            { allowSignalWrites: true }
        );

        effect(() => {
            if (this.currentUser()) {
                this.notificationApiService.getPinnedNotifications().subscribe();
            }
        });
    }

    public dismissPinnedNotification(id: string): void {
        const next = new Set(this.dismissedPinnedIds());
        next.add(id);
        this.dismissedPinnedIds.set(next);
    }

    public toggleSidebar = () => this.isSidebarOpen.set(!this.isSidebarOpen());

    public closeSidebar = () => this.isSidebarOpen.set(false);

    public toggleUserDropdown = () => this.isUserDropdownOpen.set(!this.isUserDropdownOpen());

    public closeUserDropdown = () => this.isUserDropdownOpen.set(false);

    public closeAlphaBanner = () => this.showAlphaBanner.set(false);

    public toggleSection(sectionId: string) {
        const section = this.visibleSections().find(currentSection => currentSection.id === sectionId);
        const user = this.currentUser();

        if (!section || section.pinned || !user) {
            return;
        }

        const nextState = {
            ...this.expandedSections(),
            [sectionId]: !this.expandedSections()[sectionId]
        };

        this.expandedSections.set(nextState);
        this.persistSectionState(user.id, nextState);
    }

    public isSectionExpanded(sectionId: string): boolean {
        return this.expandedSections()[sectionId] ?? false;
    }

    public isItemActive(item: SidebarItem, sectionItems: SidebarItem[] = []): boolean {
        if (!item.subNested) {
            const activeSubNested = sectionItems.some(
                sibling => sibling.subNested && this.checkRouteActive(sibling)
            );
            if (activeSubNested) return false;
        }

        return this.checkRouteActive(item);
    }

    private checkRouteActive(item: SidebarItem): boolean {
        const matchOptions = {
            paths: item.exact ? 'exact' : 'subset',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored'
        } as const;

        return this.router.isActive(
            this.router.createUrlTree([item.route], { queryParams: item.queryParams }),
            matchOptions
        );
    }

    public openProfilePage() {
        this.router.navigate(['/profile']);
        this.closeUserDropdown();
    }

    protected exitMockLogin() {
        this.closeUserDropdown();
        this.authService.exitMockLogin();
        this.router.navigate(['/']).then(() => window.location.reload());
    }

    public logout() {
        this.closeUserDropdown();
        this.authService.logout();
    }

    private findActiveSectionId(sections: SidebarSection[], currentUrl: string): string | null {
        const activeSection = sections.find(section =>
            section.items.some(item => this.isRouteActive(item, currentUrl))
        );

        return activeSection?.id ?? null;
    }

    private isRouteActive(item: SidebarItem, currentUrl: string): boolean {
        const itemUrl = this.router.serializeUrl(
            this.router.createUrlTree([item.route], { queryParams: item.queryParams })
        );

        return item.exact ? currentUrl === itemUrl : currentUrl.startsWith(itemUrl);
    }

    private readStoredSectionState(userId: string): Record<string, boolean> {
        try {
            const storedValue = localStorage.getItem(this.getSectionStorageKey(userId));

            if (!storedValue) {
                return {};
            }

            const parsedValue = JSON.parse(storedValue);
            if (!parsedValue || typeof parsedValue !== 'object') {
                return {};
            }

            return Object.entries(parsedValue).reduce<Record<string, boolean>>((state, [sectionId, value]) => {
                if (typeof value === 'boolean') {
                    state[sectionId] = value;
                }

                return state;
            }, {});
        } catch {
            return {};
        }
    }

    private persistSectionState(userId: string, state: Record<string, boolean>) {
        const persistedState = this.visibleSections().reduce<Record<string, boolean>>((currentState, section) => {
            if (!section.pinned) {
                currentState[section.id] = state[section.id] ?? false;
            }

            return currentState;
        }, {});

        localStorage.setItem(this.getSectionStorageKey(userId), JSON.stringify(persistedState));
    }

    private getSectionStorageKey(userId: string): string {
        return `${MainLayoutComponent.SECTION_STORAGE_KEY_PREFIX}${userId}`;
    }
}
